import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { track } from 'api/track';
import ApplePayButtonNew from 'components/apple-pay-button/new';
import ModalSlideUp from 'components/modal-slide-up';
import { useConfig } from 'config/use-config';
import { LOCAL_CART_KEY } from 'constants/cart';
import useApplyCoupon from 'hooks/coupon/use-apply-coupon';
import get from 'lodash/get';
import CouponModalPopUp from 'modules/checkout/components/coupon-modal-pop-up';
import { useRouter } from 'next/router';
import React, {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocalStorage, useSessionStorage } from 'react-use';
import { useTrackingStore } from 'stores/tracking';
import { Gtm } from 'tracking';
import {
  EffectiveCartPayload,
  useEffectiveCart,
  useEffectiveCartPayload,
} from 'hooks/effective-cart/useEffectiveCart';
import { DiscountOrigin, EffectiveCart } from 'interfaces/cart';
import { isGiftProductItem, isSubscriptionItem, PdProductItem } from 'interfaces/line-item';
import { Discount, DiscountType } from 'interfaces/order';
import { ShippingMethod } from 'interfaces/shipping';
import { useAuth } from '../auth';
import { CartItemInput, UpdateCartItem } from '../type/cart-type';
import { assertNoNullVariantId } from './utils';
import { updateCart } from './utils/update-cart';
import { addToCart } from 'contexts/cart/utils/add-to-cart';

export type { EffectiveCartSubscriptionItem } from './utils';

export interface CartContextInterface {
  canUseCoupon: boolean;
  cartTotal: number;
  containsPreorderItem: boolean;
  containsPreorderItemsOnly: boolean;
  containsSubscriptionItem: boolean;
  containsRedemptionItem: boolean;
  couponCode: string | null | undefined;
  discount?: Discount;
  discountOrigins?: DiscountOrigin[];
  effectiveCart: EffectiveCart | undefined;
  effectiveCartErrors?: EffectiveCart['errors'];
  effectiveCartPayload: EffectiveCartPayload;
  isBonusModalOpen: boolean;
  isCartEmpty: boolean;
  isCartReady: boolean;
  isCartUpdating: boolean;
  isCheckoutBeingCreated: boolean;
  items: EffectiveCart['items'];
  itemsCount: number;
  loading: boolean;
  shippingMethod: ShippingMethod | null;
  useCredits: boolean;
  oosItems: PdProductItem[];
  addToCart: (arg: CartItemInput[]) => void;
  updateCart: (items: UpdateCartItem[]) => void;
  goToCheckout: () => Promise<any>;
  refetchCart: () => void;
  reFetchEffectiveCart: () => Promise<EffectiveCart | undefined>;
  resetCheckout: () => void;
  setCouponCode: (code: string | null) => void;
  setExpressCheckoutCustomerId: (customerId: number | null) => void;
  setIsApplePayModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsBonusModalOpen: (v: boolean) => void;
  setShippingMethod: (shippingMethod: ShippingMethod | null) => void;
  setUseCredits: (useCredits: boolean) => void;
  flushCart: () => void;
}

const CartContext = createContext<CartContextInterface>({} as any);

export const CartContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { currencyCode, paypalClientId } = useConfig();
  const paypalExpressInitialOptions = useMemo(
    () => ({
      clientId: paypalClientId,
      currency: currencyCode,
      components: 'buttons',
      intent: 'authorize',
      commit: false,
    }),
    [currencyCode, paypalClientId]
  );

  const [isApplePayModalOpen, setIsApplePayModalOpen] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [isBonusModalOpen, setIsBonusModalOpen] = useState<boolean>(false);
  const router = useRouter();
  const { mapCheckoutItems } = useTrackingStore();
  const { isCustomerReady, shippingAddress, isSessionFetched } = useAuth();
  const [isCheckoutBeingCreated] = useState(false); // TODO: should be removed
  const [expressCheckoutCustomerId, setExpressCheckoutCustomerId] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useLocalStorage<string | null>('pd:cart-coupon-code', null);
  const [shippingMethod, setShippingMethod] = useSessionStorage<ShippingMethod | null>(
    'pd:cart-shipping',
    null
  );
  const [useCredits, setUseCredits] = useSessionStorage<boolean>('pd:checkout-use-credits', true);
  const [hasSeenAutoApplyCoupon, setHasSeenAutoApplyCoupon] = useSessionStorage<boolean>(
    'pd:has-seen-auto-apply-coupon',
    false
  );
  /**
   * Call coupon code tracking API when session is already fetched
   */
  useEffect(() => {
    if (isSessionFetched && couponCode) {
      track({ name: 'Coupon Assignment', code: couponCode });
    }
  }, [couponCode, isSessionFetched]);

  const { couponCode: couponCodeViaAudience } = useApplyCoupon();

  useEffect(() => {
    if (!router.isReady) return;

    if (couponCodeViaAudience && !hasSeenAutoApplyCoupon) {
      setCouponCode(couponCodeViaAudience);
      setShowCouponModal(true);
      setHasSeenAutoApplyCoupon(true);
    } else if (router.query && router.query.coupon_code) {
      setCouponCode(
        Array.isArray(router.query.coupon_code)
          ? router.query.coupon_code[0]
          : router.query.coupon_code
      );
      if (router.query.omitModal) return;
      setShowCouponModal(true);
    }
  }, [
    couponCodeViaAudience,
    hasSeenAutoApplyCoupon,
    router.isReady,
    router.query,
    setCouponCode,
    setHasSeenAutoApplyCoupon,
  ]);

  const [localCartInput, setLocalInput] = useLocalStorage<CartItemInput[]>(LOCAL_CART_KEY, []);
  const [cartItemInput, setCartItemInput] = useState<CartItemInput[]>(localCartInput || []);

  // TODO: Remove unnecessary duplication of localCartInput and cartItemInput.
  //  This useEffect gets value from the local storage and sets it as a state, instead we could use it directly
  //  however if we replace useState with useLocalStorage, it appears that prevCartItemInput would
  //  still keep previous version of a state, which creates bugs.
  useEffect(() => {
    setLocalInput(cartItemInput);
  }, [cartItemInput, setLocalInput]);

  const flushCart = useCallback(() => {
    setLocalInput([]);
    setCartItemInput([]);
  }, [setLocalInput]);

  const handleUpdate = useCallback((updateCartArgs: UpdateCartItem[]) => {
    setCartItemInput((prevCartItemInput) => {
      // validates items for having null in id.
      assertNoNullVariantId({
        items: updateCartArgs.map((item) => item.updateData),
        context: { cart: prevCartItemInput },
      });

      return updateCart({ prev: prevCartItemInput, updates: updateCartArgs });
    });
  }, []);

  const handleAdd = useCallback((updateCartArgs: CartItemInput[]) => {
    setCartItemInput((prevCartItemInput) => {
      assertNoNullVariantId({
        items: updateCartArgs,
        context: { cart: prevCartItemInput },
      });
      return addToCart({ prev: prevCartItemInput, updates: updateCartArgs });
    });
  }, []);

  const effectiveCartPayloadItems = useMemo(
    () => cartItemInput.map((item) => ({ ...item, variantId: Number(item.variantId) })),
    [cartItemInput]
  );

  const effectiveCartPayload = useEffectiveCartPayload({
    shippingAddress,
    shippingMethod,
    couponCode,
    useCredits,
    expressCheckoutCustomerId,
    items: effectiveCartPayloadItems,
  });

  const {
    effectiveCart,
    loading: effectiveCartLoading,
    refetch,
    error: effectiveCartError,
  } = useEffectiveCart(effectiveCartPayload);

  // The cart input the server last accepted; the snapshot we roll back to on a sync error.
  const lastAcceptedCartInput = useRef<CartItemInput[]>([]);

  useEffect(() => {
    if (effectiveCart && !effectiveCartError && !effectiveCartLoading) {
      lastAcceptedCartInput.current = cartItemInput;
    }
  }, [effectiveCart, effectiveCartError, effectiveCartLoading, cartItemInput]);

  // Roll back the unconfirmed item(s) on a rejected sync so poison self-heals instead of looping.
  useEffect(() => {
    if (!effectiveCartError) return;

    const accepted = lastAcceptedCartInput.current;
    setCartItemInput((prev) => (prev === accepted ? prev : accepted));
  }, [effectiveCartError]);

  const effectiveCartItems = useMemo(() => {
    return effectiveCart ? effectiveCart.items : [];
  }, [effectiveCart]);

  const goToCheckout = useCallback(async () => {
    await Gtm.track(
      {
        group: Gtm.Group.Cart,
        name: Gtm.CartEvent.CheckoutOptionSelect,
        data: {
          type: 'standard',
          checkout: {
            items: mapCheckoutItems(effectiveCartItems),
          },
        },
      },
      { timeout: 1000 }
    );

    router.push(`/checkout/address`);
  }, [effectiveCartItems, mapCheckoutItems, router]);

  const cartTotal = get(effectiveCart, 'cartTotal', 0);
  const itemsCount = effectiveCart?.items.reduce((pre, cur) => {
    return pre + cur.quantity;
  }, 0);

  const containsPreorderItemsOnly = useMemo(() => {
    return effectiveCartItems.every((item) => item.tags.includes('Preorder'));
  }, [effectiveCartItems]);
  const containsPreorderItem = useMemo(() => {
    return effectiveCartItems.some((item) => item.tags.includes('Preorder'));
  }, [effectiveCartItems]);
  const containsSubscriptionItem = useMemo(() => {
    return effectiveCartItems.some((item) => isSubscriptionItem(item));
  }, [effectiveCartItems]);
  const containsRedemptionItem = useMemo(() => {
    return effectiveCartItems.some((item) => isGiftProductItem(item));
  }, [effectiveCartItems]);
  const canUseCoupon = useMemo(
    () => effectiveCart?.discount?.type !== DiscountType.REFERAL,
    [effectiveCart?.discount]
  );

  const oosItems = useMemo(
    () => effectiveCartItems.filter((item) => item.tags.includes('test_oos')),
    [effectiveCartItems]
  );

  const value: CartContextInterface = {
    canUseCoupon,
    cartTotal,
    containsPreorderItem,
    containsPreorderItemsOnly,
    containsSubscriptionItem,
    containsRedemptionItem,
    couponCode,
    discount: effectiveCart && effectiveCart.discount,
    discountOrigins: effectiveCart && effectiveCart.discountOrigins,
    effectiveCart,
    effectiveCartErrors: effectiveCart && effectiveCart.errors,
    effectiveCartPayload,
    isBonusModalOpen,
    isCartEmpty: !effectiveCartLoading && effectiveCart?.items.length === 0,
    isCartReady: isCustomerReady && !effectiveCartLoading,
    isCartUpdating: effectiveCartLoading,
    isCheckoutBeingCreated,
    items: effectiveCartItems,
    itemsCount: itemsCount || 0,
    loading: effectiveCartLoading,
    shippingMethod,
    useCredits,
    oosItems,
    addToCart: handleAdd,
    updateCart: handleUpdate,
    goToCheckout,
    refetchCart: refetch,
    reFetchEffectiveCart: refetch,
    resetCheckout: refetch,
    setCouponCode,
    setExpressCheckoutCustomerId,
    setIsApplePayModalOpen,
    setIsBonusModalOpen,
    setShippingMethod,
    setUseCredits,
    flushCart,
  };

  return (
    <CartContext.Provider value={value}>
      <PayPalScriptProvider options={paypalExpressInitialOptions}>
        {children}
        {couponCode && showCouponModal && <CouponModalPopUp couponCode={couponCode} />}
        <ModalSlideUp
          open={isApplePayModalOpen}
          onClose={() => setIsApplePayModalOpen(false)}
          title={
            <div>
              <p className="mb-1">
                <FormattedMessage id="cart:apple-pay-modal:title" />
              </p>
              <p className="text-sm font-normal">
                <FormattedMessage id="cart:apple-pay-modal:content" />
              </p>
            </div>
          }
        >
          <ApplePayButtonNew className="h-10 max-w-60 grow" />
        </ModalSlideUp>
      </PayPalScriptProvider>
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextInterface => useContext(CartContext);
